/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: DynamicDestinationFormView.tsx
 🗓️ Created: 2025-01-27
 🧩 Description:
    Form view for creating and editing Dynamic Destinations.

 🔧 Notes:
    - Refactored from monolithic 1105-line file
    - Uses hooks for state management
    - Uses section components for form layout
    - Maintains QuickSearchModal functionality

 🔁 Last Updated: 2025-01-27 by Rodrigo Cuadra (Refactored)
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Share2, RefreshCw, Search, Copy, Plus } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { useNotifications } from '../../contexts/NotificationContext';
import { ModuleFormHeader } from '../../components/layouts';
import { QuickSearchModal } from '../../components/ui/QuickSearchModal';
import FixedActionBar from '../../components/ui/FixedActionBar';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import { useDynamicDestinationData } from './hooks/useDynamicDestinationData';
import { useDynamicDestinationForm } from './hooks/useDynamicDestinationForm';
import { GeneralSection, OptionsSection } from './components/DynamicDestinationSections';
import { deleteDynamicDestinationById, navigateToDynamicDestinationDuplicate } from './dynamicDestinationsActions';
import { fetchDynamicDestinations } from './api/dynamicDestinationsApi';
import { DynamicDestination } from './types';

export default function DynamicDestinationFormView() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const duplicateId = searchParams.get('duplicate');
  const isEditMode = Boolean(id && id !== 'new');
  const navigate = useNavigate();
  const { t } = useI18n();
  const { notifications, removeNotification, showSuccess } = useNotifications();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Quick List states
  const [showQuickList, setShowQuickList] = useState(false);
  const [quickListSearch, setQuickListSearch] = useState('');
  const [quickListItems, setQuickListItems] = useState<DynamicDestination[]>([]);
  const [initialItems, setInitialItems] = useState<DynamicDestination[]>([]);
  const [isLoadingInitial, setIsLoadingInitial] = useState(false);
  const [isQuickSearching, setIsQuickSearching] = useState(false);

  const { isLoading, formData: loadedFormData } = useDynamicDestinationData(id, duplicateId);

  const {
    formData,
    errors,
    optionErrors,
    isSaving,
    hasChanges,
    handleFieldChange,
    handleOptionChange,
    handleDefaultModuleChange,
    handleOptionModuleChange,
    addOption,
    removeOption,
    handleSubmit,
    handleCancel
  } = useDynamicDestinationForm(loadedFormData || null, id, isEditMode);

  // Enhanced save handler for useSaveWithAction
  const handleSaveInternal = useCallback(async () => {
    const savedData = await handleSubmit();
    // If no changes were detected, return current formData to allow navigation
    if (!savedData) {
      return formData;
    }
    return savedData;
  }, [handleSubmit, formData]);

  // Use reusable hook for save actions
  const mode = isEditMode ? 'edit' : 'create';
  const handleSaveWithAction = useSaveWithAction({
    onSave: handleSaveInternal,
    listPath: '/pbx/applications/dynamic-destination',
    newPath: '/pbx/applications/dynamic-destination/new',
    mode,
    getEditPath: (savedData) => `/pbx/applications/dynamic-destination/${savedData?.id || savedData?.uuid || id}`,
    getFormData: () => formData,
    storageKey: 'dynamicDestinations:saveAndNewData',
  });

  const pageTitle = useMemo(
    () => (isEditMode ? `${t('dynamicDestination.editEntry')} - ${formData?.name || ''}` : t('dynamicDestination.createEntry')),
    [isEditMode, t, formData?.name],
  );

  useEffect(() => {
    document.title = `${pageTitle} | Ring2All`;
  }, [pageTitle]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!isEditMode || !id) return;

    setIsDeleting(true);
    try {
      await deleteDynamicDestinationById(id);

      showSuccess(t('dynamicDestination.messages.deleteSuccess'));
      navigate('/pbx/applications/dynamic-destination');
    } catch (error) {
      // Error handled by deleteDynamicDestinationById
      setShowDeleteModal(false);
    } finally {
      setIsDeleting(false);
    }
  }, [id, isEditMode, navigate, showSuccess, t]);

  const handleDuplicateFromForm = useCallback(() => {
    if (!isEditMode || !id) return;
    navigateToDynamicDestinationDuplicate(navigate, id);
  }, [isEditMode, id, navigate]);

  // Navigate to new
  const handleAddNew = useCallback(() => {
    navigate('/pbx/applications/dynamic-destination/new');
  }, [navigate]);

  // Quick List loader
  const loadInitialItems = useCallback(async () => {
    if (initialItems.length > 0) return;

    setIsLoadingInitial(true);
    try {
      const result = await fetchDynamicDestinations({
        page: 1,
        limit: 100,
        sortBy: 'name',
        sortOrder: 'asc'
      });
      if (result.success && Array.isArray(result.data)) {
        setInitialItems(result.data);
        setQuickListItems(result.data);
      }
    } catch (error) {
      console.error('Error loading initial items:', error);
    } finally {
      setIsLoadingInitial(false);
    }
  }, [initialItems.length]);

  const loadQuickList = useCallback(
    async (search: string) => {
      const normalized = search.trim();

      if (!normalized || normalized.length < 2) {
        setQuickListItems(initialItems);
        return;
      }

      setIsQuickSearching(true);
      try {
        const result = await fetchDynamicDestinations({
          page: 1,
          limit: 50,
          sortBy: 'name',
          sortOrder: 'asc',
          search: normalized
        });
        if (result.success && Array.isArray(result.data)) {
          setQuickListItems(result.data);
        }
      } catch (error) {
        console.error('Error loading quick list items:', error);
      } finally {
        setIsQuickSearching(false);
      }
    },
    [initialItems]
  );

  useEffect(() => {
    if (showQuickList && initialItems.length === 0) {
      loadInitialItems();
    }
  }, [showQuickList, initialItems.length, loadInitialItems]);

  useEffect(() => {
    if (!showQuickList) return;

    const timer = setTimeout(() => {
      if (quickListSearch.trim().length >= 2) {
        loadQuickList(quickListSearch);
      } else {
        setQuickListItems(initialItems);
      }
    }, 800);

    return () => clearTimeout(timer);
  }, [quickListSearch, showQuickList, loadQuickList, initialItems]);

  if (isLoading || !formData) {
    return (
      <div className="flex h-64 items-center justify-center">
        <RefreshCw className="mr-2 h-4 w-4 animate-spin text-muted-foreground" />
        <span className="text-muted-foreground">{t('common.loading')}</span>
      </div>
    );
  }

  return (
    <div className="p-6 pb-24"><ModuleFormHeader
        icon={<Share2 className="h-5 w-5 text-primary" />}
        title={pageTitle}
        backButtonText={t('dynamicDestination.actions.list')}
        onBack={() => navigate('/pbx/applications/dynamic-destination')}
        actions={[
          // 1. Quick Search (always visible)
          {
            icon: <Search className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('dynamicDestination.actions.quickList'),
            onClick: () => setShowQuickList(true),
          },
          // 2. Duplicate (edit mode only)
          ...(isEditMode
            ? [
              {
                icon: <Copy className="h-5 w-5 text-muted-foreground" />,
                tooltip: t('dynamicDestination.actions.duplicate'),
                onClick: handleDuplicateFromForm,
              },
            ]
            : []),
          // 3. Add New (always visible)
          {
            icon: <Plus className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('dynamicDestination.actions.add'),
            onClick: handleAddNew,
          }
        ]}
      />

      <form className="mt-8 space-y-6">
        <GeneralSection
          formData={formData}
          errors={errors}
          onFieldChange={handleFieldChange}
          onDefaultModuleChange={handleDefaultModuleChange}
        />

        <OptionsSection
          formData={formData}
          optionErrors={optionErrors}
          onOptionChange={handleOptionChange}
          onOptionModuleChange={handleOptionModuleChange}
          onAddOption={addOption}
          onRemoveOption={removeOption}
        />
      </form>

      <FixedActionBar
        onSaveWithAction={handleSaveWithAction}
        onCancel={handleCancel}
        onDelete={isEditMode ? () => setShowDeleteModal(true) : undefined}
        isSaving={isSaving}
        isEditMode={isEditMode}
        useSaveDropdown={true}
        saveText={t('common.save')}
        cancelText={t('common.cancel')}
        deleteText={t('common.delete')}
      />

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title={t('dynamicDestination.messages.deleteTitle')}
        message={t('dynamicDestination.messages.confirmDelete', { name: formData.name || '' })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        type="danger"
        isLoading={isDeleting}
      />

      <QuickSearchModal
        isOpen={showQuickList}
        onClose={() => {
          setShowQuickList(false);
          setQuickListSearch('');
        }}
        title={t('dynamicDestination.title')}
        items={quickListItems}
        isLoadingInitial={isLoadingInitial}
        isSearching={isQuickSearching}
        searchValue={quickListSearch}
        onSearchChange={setQuickListSearch}
        onItemSelect={(item) => {
          setShowQuickList(false);
          setQuickListSearch('');
          navigate(`/pbx/applications/dynamic-destination/${item.uuid || item.id}`);
        }}
        currentItemId={id}
        renderItem={(item, isCurrent) => (
          <button
            onClick={() => {
              if (!isCurrent) {
                setShowQuickList(false);
                setQuickListSearch('');
                navigate(`/pbx/applications/dynamic-destination/${item.uuid || item.id}`);
              }
            }}
            disabled={isCurrent}
            className={`w-full text-left p-3 rounded-md mb-2 transition-colors ${isCurrent
              ? 'bg-primary/20 border-2 border-primary cursor-default'
              : 'hover:bg-gray-100 dark:hover:bg-gray-700 border border-border'
              }`}
          >
            <div className="flex items-center gap-3">
              <Share2 className="h-4 w-4 text-primary flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-foreground">
                    {item.name}
                  </span>
                  {isCurrent && (
                    <span className="px-2 py-0.5 text-xs font-medium bg-primary text-primary-foreground rounded">
                      {t('dynamicDestination.current', 'Current')}
                    </span>
                  )}
                </div>
                {item.description && (
                  <div className="text-sm text-muted-foreground truncate">
                    {item.description}
                  </div>
                )}
              </div>
            </div>
          </button>
        )}
        searchPlaceholder={t('common.searchPlaceholder')}
        minCharsMessage={t('dynamicDestination.quickListMinChars', 'Type at least 2 characters to search')}
        emptyMessage={t('dynamicDestination.quickListEmpty', 'Start typing to search dynamic destinations')}
        noResultsMessage={t('dynamicDestination.quickListNoResults', 'No dynamic destinations found')}
        headerIcon={<Share2 className="h-5 w-5" />}
        minSearchChars={2}
      />
    </div>
  );
}
